import { ChangeDetectionStrategy, Component, ViewChild } from '@angular/core';
import { IconButtonColumn, IconColumn, TableColumn, TextColumn } from '@vsolv/vectors/table';
import { SecurityService } from '@wsphere/staff/web';
import { combineLatest, map, Observable, shareReplay, switchMap } from 'rxjs';
import { CreateEmailTemplateDialog, EditEmailTemplateDialog, EmailTemplatePreviewDialog } from '../../dialogs';
import { EmailTemplate, EmailTemplatesService } from './email-templates.service';

@Component({
  changeDetection: ChangeDetectionStrategy.Eager,
  selector: 'ws-email-templates-page',
  templateUrl: './email-templates.page.html',
  standalone: false,
})
export class EmailTemplatesPage {
  constructor(
    private securitySvc: SecurityService,
    private emailTemplatesSvc: EmailTemplatesService
  ) {}

  @ViewChild(EmailTemplatePreviewDialog) emailTemplatePreviewDialog!: EmailTemplatePreviewDialog;
  @ViewChild('editEmailTemplateDialog') editEmailTemplateDialog!: EditEmailTemplateDialog;
  @ViewChild('createEmailTemplateDialog') createEmailTemplateDialog!: CreateEmailTemplateDialog;

  private readonly globalDistributors$ = this.securitySvc.globalDistributors$;

  templates$ = this.emailTemplatesSvc.getTemplatesObservable();

  selectedEmailTemplate!: EmailTemplate | null;

  canView$ = this.globalDistributors$.pipe(
    switchMap(globalDistributors =>
      this.securitySvc.hasAccess(
        'stf_ViewSettings',
        globalDistributors?.map(distributor => distributor.permissionKey) || null
      )
    ),
    shareReplay(1)
  );

  canEdit$ = this.globalDistributors$.pipe(
    switchMap(globalDistributors =>
      this.securitySvc.hasAccess(
        'stf_EditSettings',
        globalDistributors?.map(distributor => distributor.permissionKey) || null
      )
    ),
    shareReplay(1)
  );

  columns$: Observable<TableColumn<unknown>[]> = combineLatest([this.canView$, this.canEdit$]).pipe(
    map(([canView, canEdit]) => {
      return [
        new IconColumn<EmailTemplate>({ fitContent: true, align: 'center', customCellClasses: 'pr-0' }, item => ({
          icon: item.enabled ? 'check-circle' : null,
          hideBackground: true,
          theme: 'default',
          size: 18,
        })),
        new TextColumn<EmailTemplate>({ header: 'Event' }, item => ({
          text: item.type,
          classes: 'truncate',
        })),
        new TextColumn<EmailTemplate>({ header: 'Delivery' }, () => ({
          text: 'Event Driven',
          classes: 'truncate',
        })),
        new TextColumn<EmailTemplate>({ header: 'Template Label' }, item => ({
          text: item.templateLabel ?? '',
          classes: 'max-w-[200px] truncate',
        })),
        new TextColumn<EmailTemplate>({ header: 'Subject' }, item => ({
          text: item.subject ?? '',
          classes: 'max-w-[200px] truncate',
        })),
        new TextColumn<EmailTemplate>({ header: 'Byline' }, item => ({
          text: item.byline ?? '',
          classes: 'max-w-[200px] truncate',
        })),
        new TextColumn<EmailTemplate>({ header: 'Content' }, item => ({
          text: this.toPlainText(item.content),
          classes: 'max-w-[200px] truncate',
        })),
        ...(canView
          ? [
              new IconButtonColumn<EmailTemplate>({ stickyEnd: true, fitContent: true }, item => ({
                type: 'clear',
                rounded: true,
                icon: 'eye',
                click: () => this.openEmailPreviewDialog(item),
              })),
            ]
          : []),
        ...(canEdit
          ? [
              new IconButtonColumn<EmailTemplate>({ stickyEnd: true, fitContent: true }, item => ({
                type: 'clear',
                rounded: true,
                icon: 'edit-01',
                click: () => this.openEditDialog(item),
              })),
            ]
          : []),
      ];
    })
  );

  openEmailPreviewDialog(template: EmailTemplate) {
    this.emailTemplatePreviewDialog.open(template);
  }

  openEditDialog(template: EmailTemplate) {
    this.selectedEmailTemplate = template;
    this.editEmailTemplateDialog?.open(template);
  }

  openCreateDialog() {
    this.createEmailTemplateDialog?.open();
  }

  private toPlainText(value: string | null) {
    return (
      value
        ?.replace(/<[^>]*>?/gm, '')
        .replace(/&nbsp;/g, ' ')
        .replace(/&lt;/g, '<')
        .replace(/&gt;/g, '>')
        .replace(/&amp;/g, '&') ?? ''
    );
  }
}
